import { useWorkflow } from 'features/libra/WorkflowContext'
import { createContext, FC, useContext, useMemo } from 'react'

import { LibraTicket } from './libraTicketsApi'

export interface TicketStepContextType {
  isActiveStep: boolean
  hasMoreSteps: boolean
  isStepComplete: boolean
  isWorkflowComplete: boolean
}

const TicketStepContext = createContext<TicketStepContextType>({
  isActiveStep: false,
  hasMoreSteps: false,
  isStepComplete: false,
  isWorkflowComplete: false,
})

export const TicketStepContextProvider: FC<{
  ticket: LibraTicket
  activeStepId: string
  children: React.ReactNode
}> = ({ children, ticket, activeStepId }) => {
  const workflow = useWorkflow()
  return (
    <TicketStepContext.Provider
      value={useMemo(() => {
        const stepIdx = workflow.stepIndices.get(activeStepId) ?? -1
        const ticketStepIdx = workflow.stepIndices.get(ticket.stepId) ?? -1
        return {
          isActiveStep:
            ticket.stepId === activeStepId && !ticket.workflowComplete,
          hasMoreSteps: stepIdx < workflow.steps.length - 1,
          isStepComplete: stepIdx < ticketStepIdx || ticket.workflowComplete,
          isWorkflowComplete: ticket.workflowComplete ?? false,
        }
      }, [
        activeStepId,
        ticket.stepId,
        ticket.workflowComplete,
        workflow.stepIndices,
        workflow.steps.length,
      ])}>
      {children}
    </TicketStepContext.Provider>
  )
}

export const useTicketStepContext = () => {
  return useContext(TicketStepContext)
}
